import { useState } from 'react';
import { api, type RangeSession } from '../lib/api';
import { errorText } from '../lib/errorText';

interface Props {
  session: RangeSession;
  /**
   * The stage that just finished (so "Nochmal" can call nextRound on it).
   * Pass null to skip the same-drill option (e.g. when user got here
   * via roster navigation rather than a fresh drill completion).
   */
  stageId: string | null;
  stageName: string;
  /** Called after nextRound is generated. */
  onSameDrill: (stageId: string) => void;
  /** Take the user back to drill picker for a different drill. */
  onDifferentDrill: () => void;
  /** Close the session ("Badeschluss") and return to session picker. */
  onBadeschluss: () => Promise<void>;
  /** "← Übersicht": back to the roster. The button is hidden when omitted. */
  onOverview?: () => void;
}

export function RoundCompleteScreen({
  stageName,
  stageId,
  onSameDrill,
  onDifferentDrill,
  onBadeschluss,
  onOverview,
}: Props) {
  const [busy, setBusy] = useState<'same' | 'badeschluss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which action failed, so "Erneut versuchen" can repeat it.
  const [failed, setFailed] = useState<'same' | 'badeschluss' | null>(null);

  async function repeat() {
    if (!stageId) return;
    setBusy('same');
    setError(null);
    try {
      const result = await api.nextRound(stageId);
      onSameDrill(result.stage.id);
    } catch (err) {
      setError(errorText(err));
      setFailed('same');
    } finally {
      setBusy(null);
    }
  }

  // No native confirm here: the Badeschluss signature overlay already asks.
  async function close() {
    setBusy('badeschluss');
    setError(null);
    try {
      await onBadeschluss();
    } catch (err) {
      setError(errorText(err));
      setFailed('badeschluss');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="space-y-4">
      {onOverview && (
        <button
          onClick={onOverview}
          disabled={busy !== null}
          className="min-h-12 pr-3 text-sm text-zinc-500 underline disabled:opacity-50"
        >
          ← Übersicht
        </button>
      )}
      <header className="rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-4">
        <h2 className="text-2xl font-bold">✓ Runde abgeschlossen</h2>
        <p className="mt-1 text-sm text-zinc-400">{stageName}</p>
      </header>

      <p className="text-sm text-zinc-400">Wie weiter?</p>

      {error && (
        <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => void (failed === 'badeschluss' ? close() : repeat())}
            disabled={busy !== null}
            className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40 disabled:opacity-50"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="space-y-2">
        {stageId && (
          <button
            onClick={() => void repeat()}
            disabled={busy !== null}
            className="w-full rounded-lg bg-emerald-700 px-4 py-5 text-lg font-bold text-white hover:bg-emerald-600 active:bg-emerald-800 disabled:opacity-50"
          >
            {busy === 'same' ? 'Erzeuge…' : '↻ Nochmal dieselbe Übung'}
          </button>
        )}
        <button
          onClick={onDifferentDrill}
          disabled={busy !== null}
          className="w-full rounded-lg border border-zinc-600 px-4 py-5 text-lg font-bold text-zinc-100 hover:border-emerald-500 hover:text-emerald-300 disabled:opacity-50"
        >
          ⚙ Andere Übung
        </button>
        <button
          onClick={() => void close()}
          disabled={busy !== null}
          className="w-full rounded-lg border border-amber-700 bg-amber-900/30 px-4 py-5 text-lg font-bold text-amber-200 hover:border-amber-500 disabled:opacity-50"
        >
          {busy === 'badeschluss' ? 'Schließe…' : '🛁 Badeschluss'}
        </button>
      </div>
    </section>
  );
}
